/**
 * デザイナー個人の「1 軸 × (個人 vs 全体平均)」推移折線。
 *
 * MyPage スキル評価タブの推移モードで、軸スイッチで選んだ 1 軸について個人 (軸色・実線) と
 * 全体平均 (灰・破線) を period 推移で重ねて表示する。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business デザイナー個人 vs 全体平均の 1 軸推移折線 (月/Q)
 * @graph-connects none
 */
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip as RTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { RADAR_PREV_COLOR, type AxisCategory } from '../idt-kpi/designEvaluation.js';
import type { AxisValues } from '../idt-kpi/DesignEvaluationRadar.js';

/**
 * 期間キー → 軸別値 の索引 (1 系列ぶん)。
 *
 * radar と同じ理由で「評価点オブジェクト + どのフィールドを見るか」ではなく値レコードで
 * 受ける。デザイン評価 (解像度 / 貢献度) と DLE 評価 (4 軸) が同じ折線描画を共有できる。
 * @graph-connects none
 */
export type AxisValuesByPeriod = Map<string, AxisValues<string>>;

/** 推移軸スイッチの 1 項目。 @graph-connects none */
export interface SwitchableAxis {
  key: string;
  /** 折線色 (個人) */
  color: string;
  /** i18n ラベルキー */
  labelKey: string;
  /** y 軸スケール ([下限, 満点]) */
  domain: [number, number];
  /** 軸の分類 (解像度のみ: 企画/設計)。pill スイッチの行分けに使う */
  category?: AxisCategory;
}

interface AxisTrendRow {
  label: string;
  full: string;
  self: number | null;
  overall: number | null;
}

interface AxisTrendTooltipEntry {
  name?: string;
  value?: number | null;
  color?: string;
  payload?: AxisTrendRow;
}

/** @graph-connects none */
function AxisTrendTooltip(props: { active?: boolean; payload?: AxisTrendTooltipEntry[] }) {
  if (!props.active || !props.payload || props.payload.length === 0) return null;
  const full = props.payload[0]?.payload?.full;
  return (
    <div className="px-3 py-1.5 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-elevated)] shadow-lg text-xs">
      <div className="text-[var(--color-text-muted)] mb-1">{full}</div>
      {props.payload.map((e) => (
        <div key={e.name} className="tabular-nums" style={{ color: e.color }}>
          {e.name}: {e.value == null ? '—' : Number(e.value).toFixed(2)}
        </div>
      ))}
    </div>
  );
}

/**
 * 1 軸の「個人 (軸色・実線) vs 全体平均 (灰・破線)」推移折線。
 * @graph-connects none
 */
export function AxisTrend({
  axis,
  selfByPeriod,
  overallByPeriod,
  periods,
  shortLabelOf,
  fullLabelOf,
  height,
  selfLabel,
  overallLabel,
}: {
  axis: SwitchableAxis;
  selfByPeriod: AxisValuesByPeriod;
  overallByPeriod: AxisValuesByPeriod;
  periods: string[];
  shortLabelOf: (period: string) => string;
  fullLabelOf: (period: string) => string;
  height: number;
  /** 個人系列の凡例・tooltip 名 */
  selfLabel: string;
  /** 全体平均系列の凡例・tooltip 名 */
  overallLabel: string;
}) {
  const { key } = axis;
  const rows = useMemo<AxisTrendRow[]>(() => {
    const valueAt = (m: AxisValuesByPeriod, per: string): number | null =>
      m.get(per)?.[key] ?? null;
    return periods.map((per) => ({
      label: shortLabelOf(per),
      full: fullLabelOf(per),
      self: valueAt(selfByPeriod, per),
      overall: valueAt(overallByPeriod, per),
    }));
  }, [key, selfByPeriod, overallByPeriod, periods, shortLabelOf, fullLabelOf]);

  return (
    <div style={{ width: '100%', height }}>
      <ResponsiveContainer>
        <LineChart data={rows} margin={{ top: 12, right: 16, bottom: 4, left: 4 }}>
          <CartesianGrid stroke="var(--color-border)" strokeDasharray="0" vertical={false} />
          <XAxis
            dataKey="label"
            tick={{ fill: 'var(--color-text-muted)', fontSize: 10 }}
            tickLine={false}
            axisLine={{ stroke: 'var(--color-border)' }}
            interval={0}
            minTickGap={0}
          />
          <YAxis
            domain={axis.domain}
            tickCount={axis.domain[1] - axis.domain[0] + 1}
            tick={{ fill: 'var(--color-text-muted)', fontSize: 10 }}
            tickLine={false}
            axisLine={false}
            width={32}
          />
          <RTooltip
            cursor={{ stroke: 'var(--color-border-hover)', strokeWidth: 1 }}
            content={<AxisTrendTooltip />}
          />
          <Line
            type="monotone"
            dataKey="overall"
            name={overallLabel}
            stroke={RADAR_PREV_COLOR}
            strokeWidth={1.5}
            strokeDasharray="5 4"
            dot={false}
            connectNulls
            isAnimationActive={false}
          />
          <Line
            type="monotone"
            dataKey="self"
            name={selfLabel}
            stroke={axis.color}
            strokeWidth={2.5}
            dot={{ r: 2.5, fill: 'var(--color-bg-surface)', stroke: axis.color, strokeWidth: 1.5 }}
            connectNulls
            isAnimationActive
            animationDuration={700}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

/** 個人(色) vs 全体(灰) の凡例。radar/推移で共有する。 @graph-connects none */
export function SelfVsOverallLegend({
  color,
  selfLabel,
  overallLabel,
}: {
  color: string;
  selfLabel: string;
  overallLabel: string;
}) {
  return (
    <div className="flex items-center justify-center gap-4 text-xs text-[var(--color-text-muted)]">
      <span className="flex items-center gap-1.5">
        <span
          className="inline-block w-3 h-0.5 rounded"
          style={{ background: color }}
          aria-hidden="true"
        />
        {selfLabel}
      </span>
      <span className="flex items-center gap-1.5">
        <span
          className="inline-block w-3 h-0.5 rounded"
          style={{ background: RADAR_PREV_COLOR }}
          aria-hidden="true"
        />
        {overallLabel}
      </span>
    </div>
  );
}

/**
 * 軸スイッチ pill 群 + 1 軸の個人 vs 全体 推移 (1 列ぶん)。
 *
 * 軸群が 1 つしかない評価 (DLE 評価の 4 軸) は、`LineComparisonView` を通さず
 * この列を 1 本だけ置いて使う。
 * @graph-connects none
 */
export function AxisTrendColumn({
  title,
  axes,
  selectedKey,
  onSelect,
  selfByPeriod,
  overallByPeriod,
  periods,
  shortLabelOf,
  fullLabelOf,
  selfLabel,
  overallLabel,
}: {
  title: string;
  axes: SwitchableAxis[];
  selectedKey: string;
  onSelect: (key: string) => void;
  selfByPeriod: AxisValuesByPeriod;
  overallByPeriod: AxisValuesByPeriod;
  periods: string[];
  shortLabelOf: (period: string) => string;
  fullLabelOf: (period: string) => string;
  selfLabel: string;
  overallLabel: string;
}) {
  const { t } = useTranslation();
  const axis = axes.find((a) => a.key === selectedKey) ?? axes[0];
  const pillClass = (active: boolean) =>
    `px-2.5 py-1 rounded-full text-[11px] font-semibold transition-colors leading-none border ${
      active
        ? 'text-[var(--color-bg-surface)]'
        : 'text-[var(--color-text-secondary)] border-[var(--color-border)] hover:border-[var(--color-border-hover)]'
    }`;
  if (!axis) return null;
  // 分類 (category) ごとに行を分ける (解像度: 企画3 上 / 設計2 下、貢献度: 分類なし=1行)。
  const pillRows: SwitchableAxis[][] = [];
  for (const a of axes) {
    const cat = a.category ?? '';
    const last = pillRows[pillRows.length - 1];
    if (last && (last[0]?.category ?? '') === cat) last.push(a);
    else pillRows.push([a]);
  }
  return (
    <div className="flex flex-col gap-2">
      <h4 className="text-xs font-semibold text-[var(--color-text-secondary)] text-center">
        {title}
      </h4>
      <div className="flex flex-col items-center gap-1.5">
        {pillRows.map((row) => (
          <div
            key={row.map((a) => a.key).join('-')}
            className="flex flex-wrap items-center justify-center gap-1.5"
          >
            {row.map((a) => {
              const active = a.key === axis.key;
              return (
                <button
                  key={a.key}
                  type="button"
                  onClick={() => {
                    onSelect(a.key);
                  }}
                  className={pillClass(active)}
                  style={active ? { background: a.color, borderColor: a.color } : undefined}
                >
                  {t(a.labelKey)}
                </button>
              );
            })}
          </div>
        ))}
      </div>
      {/* pill 行数が列間で異なっても x 軸・凡例が水平に揃うよう、グラフ+凡例を最下段へ寄せる */}
      <div className="mt-auto flex flex-col gap-2">
        <AxisTrend
          axis={axis}
          selfByPeriod={selfByPeriod}
          overallByPeriod={overallByPeriod}
          periods={periods}
          shortLabelOf={shortLabelOf}
          fullLabelOf={fullLabelOf}
          height={340}
          selfLabel={selfLabel}
          overallLabel={overallLabel}
        />
        <SelfVsOverallLegend color={axis.color} selfLabel={selfLabel} overallLabel={overallLabel} />
      </div>
    </div>
  );
}

/**
 * 推移モード本体: PJ解像度 (左) と チーム貢献度 (右) の折線を 2 列で同時表示する。
 * 各列が独立した軸スイッチを持ち、解像度 1 軸と貢献度 1 軸を並べて比較できる。
 * @graph-connects none
 */
export function LineComparisonView({
  resolutionTitle,
  evaluationTitle,
  resolutionAxes,
  evaluationAxes,
  selfByPeriod,
  overallByPeriod,
  periods,
  shortLabelOf,
  fullLabelOf,
  selfLabel,
  overallLabel,
}: {
  resolutionTitle: string;
  evaluationTitle: string;
  resolutionAxes: SwitchableAxis[];
  evaluationAxes: SwitchableAxis[];
  selfByPeriod: AxisValuesByPeriod;
  overallByPeriod: AxisValuesByPeriod;
  periods: string[];
  shortLabelOf: (period: string) => string;
  fullLabelOf: (period: string) => string;
  selfLabel: string;
  overallLabel: string;
}) {
  const [resKey, setResKey] = useState<string>(resolutionAxes[0]?.key ?? '');
  const [evalKey, setEvalKey] = useState<string>(evaluationAxes[0]?.key ?? '');
  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
      <AxisTrendColumn
        title={resolutionTitle}
        axes={resolutionAxes}
        selectedKey={resKey}
        onSelect={setResKey}
        selfByPeriod={selfByPeriod}
        overallByPeriod={overallByPeriod}
        periods={periods}
        shortLabelOf={shortLabelOf}
        fullLabelOf={fullLabelOf}
        selfLabel={selfLabel}
        overallLabel={overallLabel}
      />
      <AxisTrendColumn
        title={evaluationTitle}
        axes={evaluationAxes}
        selectedKey={evalKey}
        onSelect={setEvalKey}
        selfByPeriod={selfByPeriod}
        overallByPeriod={overallByPeriod}
        periods={periods}
        shortLabelOf={shortLabelOf}
        fullLabelOf={fullLabelOf}
        selfLabel={selfLabel}
        overallLabel={overallLabel}
      />
    </div>
  );
}
